import { createApp, provide, h } from 'vue';
import { useAlertStore } from '@/stores';
import { pinia } from '@/stores/pinia';

import App from './App.vue';
import { router } from './router';

import { fas } from '@fortawesome/free-solid-svg-icons';
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import helper from '@/helpers/helper';
import dataHelper from '@/helpers/data';
import optionsHelper from '@/helpers/options';

import { DefaultApolloClient } from '@vue/apollo-composable';
import { apolloClient } from './apollo-client';
import { createApolloProvider } from '@vue/apollo-option';
import VueApolloComponents from '@vue/apollo-components';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';

if (process.env.NODE_ENV === 'development') {
  // Without this, Apollo Client ships only numeric invariant codes (e.g.
  // "message":42) with a https://go.apollo.dev/c/err#... link instead of the
  // real error text -- these two calls restore the actual readable messages
  // in development.
  loadDevMessages();
  loadErrorMessages();
}

import 'vuetify/styles';
import { createVuetify } from 'vuetify';
import * as components from 'vuetify/components';
import * as directives from 'vuetify/directives';
import { aliases as mdiAliases, mdi } from 'vuetify/iconsets/mdi';
import { aliases as faAliases, fa } from 'vuetify/iconsets/fa';

import 'bootstrap/dist/js/bootstrap.js';
import '@/assets/js/main';

// Add Font Awesome icons to the library
library.add(fas);

// Create Vuetify instance with MDI and FontAwesome icons
const vuetify = createVuetify({
  components,
  directives,
  icons: {
    defaultSet: 'fa',
    aliases: {
      ...mdiAliases,
      ...faAliases,
    },
    sets: {
      mdi,
      fa,
    },
  },
  theme: {
    defaultTheme: 'stockProfile',
    themes: {
      stockProfile: {
        dark: false,
        colors: {
          primary: '#2563EB',
          secondary: '#6B7280',
          success: '#16A34A',
          error: '#DC2626',
          warning: '#D97706',
          info: '#0EA5E9',
          background: '#F7F8FA',
          surface: '#FFFFFF',
        },
      },
    },
  },
});

// Create Apollo provider
const apolloProvider = createApolloProvider({
  defaultClient: apolloClient,
});

const app = createApp({
  setup() {
    provide(DefaultApolloClient, apolloClient);
  },
  render: () => h(App),
});

// Register global plugins and components
app
  .use(pinia)
  .use(router)
  .use(apolloProvider)
  .use(VueApolloComponents)
  .use(helper)
  .use(dataHelper)
  .use(optionsHelper)
  .use(vuetify);

// Register global FontAwesome component
app.component('font-awesome-icon', FontAwesomeIcon);

// Set alertStore globally
app.config.globalProperties.$alertStore = useAlertStore(pinia);

// Mount app
app.mount('#app');
